import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  Select,
  Spin,
  Tooltip,
  message,
} from "antd";
import { ReloadOutlined, CheckCircleFilled } from "@ant-design/icons";
import dayjs from "dayjs";
import SymbolSelector from "./SymbolSelector";
import { apiGetOptionExpirations, apiGetOptionChain } from "api/apiOptions";

const { Text } = Typography;
const { Option } = Select;

const DEFAULT_RISK_FREE_RATE = 4.5;

const formatExpirationLabel = (exp) => {
  const date = dayjs(exp, "YYYYMMDD");
  return date.isValid() ? `${exp} (${date.format("ddd")})` : exp;
};

const OptionsInputForm = ({
  selectedSymbol,
  onSymbolChange,
  onCalculate,
  onExpirationSelect,
}) => {
  const [expirationsList, setExpirationsList] = useState([]);
  const [selectedExpiration, setSelectedExpiration] = useState(null);
  const [expirationsLoading, setExpirationsLoading] = useState(false);
  const [chainLoading, setChainLoading] = useState(false);
  const [chainLoaded, setChainLoaded] = useState(false);

  const resetFlow = () => {
    setExpirationsList([]);
    setSelectedExpiration(null);
    setChainLoaded(false);
  };

  const handleSymbolChange = (symbol) => {
    resetFlow();
    onSymbolChange(symbol);
  };

  // Fetch the option chain for a specific expiration — only called once the
  // user has explicitly picked one, or wants to refresh the current one.
  const fetchChainForExpiration = async (symbol, expiration) => {
    setChainLoading(true);
    try {
      const chain = await apiGetOptionChain(symbol, expiration);
      setChainLoaded(true);

      onCalculate({
        underlyingPrice: chain.underlyingPrice,
        expiration: chain.expiration,
        chainStrikes: chain.strikes,
        time: chain.time,
        riskFreeRate: DEFAULT_RISK_FREE_RATE,
      });
    } catch (error) {
      console.error("Error fetching option chain:", error);
      message.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          `Failed to fetch option chain for ${expiration}`,
      );
    } finally {
      setChainLoading(false);
    }
  };

  const handleExpirationSelect = (expiration) => {
    setSelectedExpiration(expiration);
    setChainLoaded(false);
    if (onExpirationSelect) onExpirationSelect(expiration);
    if (selectedSymbol && expiration) {
      fetchChainForExpiration(selectedSymbol, expiration);
    }
  };

  // Fetch only the list of available expirations whenever the symbol
  // changes — the chain itself is fetched only once the user picks one.
  useEffect(() => {
    if (!selectedSymbol) return undefined;

    let cancelled = false;

    (async () => {
      setExpirationsLoading(true);
      try {
        const { expirations } = await apiGetOptionExpirations(selectedSymbol);
        if (cancelled) return;

        setExpirationsList(expirations || []);

        if (!expirations?.length) {
          message.error(`No live expirations available for ${selectedSymbol}`);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching option expirations:", error);
        message.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            `Failed to fetch expirations for ${selectedSymbol}`,
        );
      } finally {
        if (!cancelled) setExpirationsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedSymbol]);

  return (
    <Card style={{ marginBottom: 24 }}>
      <Row gutter={[16, 16]} align="bottom">
        <Col xs={24} sm={12} md={8}>
          <SymbolSelector
            selectedSymbol={selectedSymbol}
            onSymbolChange={handleSymbolChange}
            disabled={expirationsLoading || chainLoading}
          />
        </Col>

        {selectedSymbol && (
          <Col xs={24} sm={12} md={16}>
            <Text strong>
              Expiration: {expirationsLoading && <Spin size="small" />}
            </Text>
            <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
              <Select
                style={{ flex: 1 }}
                placeholder="Select expiration"
                value={selectedExpiration}
                onChange={handleExpirationSelect}
                disabled={
                  expirationsLoading || chainLoading || !expirationsList.length
                }
                loading={expirationsLoading}
              >
                {expirationsList.map((exp) => (
                  <Option key={exp} value={exp}>
                    {formatExpirationLabel(exp)}
                  </Option>
                ))}
              </Select>
              {selectedExpiration && (
                <Tooltip title="Refresh chain data for this expiration">
                  <Button
                    icon={<ReloadOutlined />}
                    size="small"
                    loading={chainLoading}
                    onClick={() =>
                      fetchChainForExpiration(
                        selectedSymbol,
                        selectedExpiration,
                      )
                    }
                  />
                </Tooltip>
              )}
            </div>
          </Col>
        )}
      </Row>

      {chainLoaded && (
        <Row style={{ marginTop: 12 }}>
          <Col span={24}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <CheckCircleFilled style={{ color: "#52c41a", marginRight: 6 }} />
              Live chain loaded — each strike priced with its own live IV.
              Risk-Free Rate, Underlying Price and Reference Time are adjustable
              in the controls below and recalculate instantly, no re-fetch.
            </Text>
          </Col>
        </Row>
      )}
    </Card>
  );
};

export default OptionsInputForm;
